/**
 * Zustand middleware utilities
 * Provides conditional devtools middleware for development environments
 */

import { devtools as devtoolsMiddleware } from 'zustand/middleware';

import { isDev } from '@tankef/env';

import type { StateCreator } from 'zustand';

/**
 * Conditional devtools middleware for Zustand stores
 * Only applies in development when Redux DevTools extension is available
 *
 * Prevents warnings: "[zustand devtools middleware] Please install/enable Redux devtools extension"
 *
 * This wrapper ensures consistent devtools behavior across all stores by:
 * - Checking if Redux DevTools extension is available
 * - Only enabling in development mode
 * - Wrapping both simple and middleware-composed creators
 *
 * Usage Examples:
 *
 * Simple store:
 * ```tsx
 * export const useMyStore = create<State>()(
 *   withDevtools('MyStoreName', (set) => ({...}))
 * );
 * ```
 *
 * With persist middleware:
 * ```tsx
 * export const useMyStore = create<State>()(
 *   withDevtools('MyStoreName',
 *     persist((set) => ({...}), { name: 'store-key' })
 *   )
 * );
 * ```
 *
 * With custom options:
 * ```tsx
 * export const useMyStore = create<State>()(
 *   withDevtools('MyStoreName', (set) => ({...}), { trace: true })
 * );
 * ```
 */
/**
 * Wraps a StateCreator with devtools middleware, handling both simple creators
 * and creators wrapped in other middleware (like persist).
 *
 * Only applies in development when Redux DevTools extension is available.
 *
 * @param name - Store name for devtools debugging
 * @param creator - Zustand state creator (can be wrapped in other middleware)
 * @param options - Optional devtools options (e.g., enabled)
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface WithDevtoolsOptions {
  enabled?: boolean;
  [key: string]: any;
}

export function withDevtools<T>(
  name: string,
  creator: StateCreator<T> | StateCreator<T, any, any>,
  options?: WithDevtoolsOptions
): StateCreator<T> | StateCreator<T, any, any> {
  // Only apply devtools in development
  if (!isDev) {
    return creator;
  }

  // Check if Redux DevTools extension is available
  const hasReduxDevtools =
    typeof window !== 'undefined' &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ !== undefined;

  // If devtools not available, return creator without middleware
  if (!hasReduxDevtools) {
    return creator;
  }

  // Merge default options with provided options
  const devtoolsOptions = {
    name,
    ...options,
  };

  // Apply devtools middleware with proper name and options
  return devtoolsMiddleware(creator as any, devtoolsOptions) as any;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
